import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    TextField,
    MenuItem,
    Button,
    Box,
    Snackbar,
    Alert,
    Autocomplete,
    Chip,
} from "@mui/material";
import styled from "@emotion/styled";
import { fetchUm, fetchFormato, createGiacenza, updateGiacenzaData, singleGiacenza, fetchGiacenze, fetchMotivazione } from "../../../features/giacenze/giacenzeThunk";
import { useDispatch, useSelector } from "react-redux";
import { selectFormato, selectisLoading, selectMotivazione, selectUm } from "../../../features/giacenze/giacenzeSlice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import 'dayjs/locale/it';
import { selectProdotti, selectSelectedProdottoId } from "../../../features/Prodotti/prodottiSlice";
import { fetchProdotti } from "../../../features/Prodotti/prodottiThunk";
import { fetchStabilimenti } from "../../../features/stabilimenti/stabilimentiThunk";
import { selectStabilimenti } from "../../../features/stabilimenti/stabilimentiSlice";
dayjs.locale('it');

const Textarea = styled.textarea`
    box-sizing: border-box;
    width: 100%;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 12px;
    border-radius: 5px;
    color: #160A2A;
    background: #fff;
    border: 2px solid #e5e5e5;
    min-height: 100px;
    resize: vertical;
`;

const schema = yup.object().shape({
    name: yup.string().required("Nome is required"),
    tipo: yup.string().required("Tipo is required"),
    lotto: yup.string().required("Lotto is required"),
    um: yup.string().required("Um is required"),
    displonibile: yup
        .number()
        .required("Q.ta disponibile is required")
        .positive("Must be a positive number"),
    formato: yup.string().required("Formato is required"),
    stato: yup.string().required("Stato is required"),
    data_ingresso: yup.date().required("Data ingresso is required"),
    scadenza: yup.date().required("Scadenza is required"),
     
    motivazione: yup.string().required("Motivazione is required")
});

const GiacenzeForm = ({ cantine, setCantine, singleData }) => {
    const dispatch = useDispatch();
    const isLoading = useSelector(selectisLoading);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    
    // Selectors for dropdown data
    const stabili_id = localStorage.getItem('stabili_id');
    const product_id = localStorage.getItem('product_id');

    const prodottiOptions = useSelector(selectProdotti);
    const stabiliOptions = useSelector(selectStabilimenti);


    const umOptions = useSelector(selectUm);
    const formatoOptions = useSelector(selectFormato);
    const motivazioneOptions = useSelector(selectMotivazione);
    const selectedProdottoId = useSelector(selectSelectedProdottoId);

    const tipo = ['Entrata', 'Uscita'];
    const stato = ['Disponibile', 'Non disponibile'];

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
        getValues
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            tipo: "",
            lotto: "",
            um: "",
            displonibile: "",
            formato: "",
            stato: "",
            data_ingresso: null,
            scadenza: null,
            note: "",
            motivazione: ""
        }
    });

    // Watch tipo field to change status display color
    const watchedTipo = watch("tipo");

    // Fetch data on component mount
    useEffect(() => {
        dispatch(fetchUm());
        dispatch(fetchFormato());
        dispatch(fetchMotivazione());
    }, [dispatch]);

    useEffect(() => {
        if (stabili_id) {
            dispatch(fetchProdotti());
        }
        if (product_id) {
            dispatch(fetchStabilimenti());
        }
    }, [dispatch, stabili_id, product_id]);


    // Set form values when in edit mode
    useEffect(() => {
        if (cantine && singleData) {
            setValue("name", singleData?.name || "");
            setValue("tipo", singleData?.tipo || "");
            setValue("lotto", singleData?.lotto || "");
            setValue("um", singleData?.um || "");
            setValue("displonibile", singleData?.displonibile || "");
            setValue("formato", singleData?.formato || "");
            setValue("stato", singleData?.stato || "");
            setValue("data_ingresso", singleData?.data_ingresso ? dayjs(singleData.data_ingresso) : null);
            setValue("scadenza", singleData?.scadenza ? dayjs(singleData.scadenza) : null);
            setValue("note", singleData?.note || "");
            setValue("motivazione", singleData?.motivazione || "");
            
            if (singleData.product_id) {
                const selectedProdotto = prodottiOptions.find(
                  (option) => option.id === singleData.product_id
                );
                setValue("prodotti_id", selectedProdotto || null);
              }
          
              // Set Stabilimento if stabili_id exists in singleData
              if (singleData.stabili_id) {
                const selectedStabili = stabiliOptions.find(
                  (option) => option.id === singleData.stabili_id
                );
                setValue("stabili_id", selectedStabili || null);
              }
        }
    }, [singleData, setValue, prodottiOptions, stabiliOptions, cantine]);

    const onSubmit = async (data) => {
        const formData = new FormData();

        if (product_id) {
            formData.append("product_id", product_id);
        }
        if (stabili_id) {
            formData.append("stabili_id", stabili_id);
        }
        
        formData.append("company_id", localStorage.getItem("company_id"));
        // formData.append("product_id", selectedProdottoId);
        formData.append("name", data.name);
        formData.append("tipo", data.tipo);
        formData.append("lotto", data.lotto);
        formData.append("um", data.um);
        formData.append("displonibile", data.displonibile);
        formData.append("formato", data.formato);
        formData.append("stato", data.stato);
        formData.append("data_ingresso", data.data_ingresso ? dayjs(data.data_ingresso).format("YYYY-MM-DD") : null);
        formData.append("scadenza", data.scadenza ? dayjs(data.scadenza).format("YYYY-MM-DD") : null);
        formData.append("note", data.note || "");
        formData.append("motivazione", data.motivazione);
        if (data.prodotti_id) {
            formData.append("product_id", data.prodotti_id.id);
        }
        if (data.stabili_id) {
            formData.append("stabili_id", data.stabili_id.id);
        }

        if (cantine) {
            // Edit Mode
            dispatch(
                updateGiacenzaData({
                    id: singleData.id,
                    data: formData,
                })
            )
                .unwrap()
                .then(() => {
                    setCantine(false);
                    dispatch(singleGiacenza(singleData.id));
                    setOpenSnackbar(true);
                    localStorage.removeItem("structureGiacenze");
                })
                .catch((err) => {
                    console.error("Update Giacenza Error:", err);
                });
        } else {
            // Create Mode
            dispatch(createGiacenza(formData))
                .unwrap()
                .then(() => {
                    setOpenSnackbar(true);
                    setCantine(false);
                    localStorage.removeItem("structureGiacenze");
                    reset();
                })
                .catch((err) => {
                    console.error("Create Giacenza Error:", err);
                });
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    // Get color based on tipo value
    const getTipoColor = (tipo) => {
        return tipo === 'Entrata' ? 'success' : tipo === 'Uscita' ? 'error' : 'default';
    };

    // Function to render tipo as a colored chip
    const renderTipoChip = (tipo) => {
        if (!tipo) return null;
        
        return (
            <Chip 
                label={tipo}
                color={getTipoColor(tipo)}
                sx={{ 
                    fontWeight: 'bold',
                    color: tipo === 'Entrata' ? '#1b5e20' : tipo === 'Uscita' ? '#d32f2f' : 'inherit',
                    backgroundColor: tipo === 'Entrata' ? '#e8f5e9' : tipo === 'Uscita' ? '#ffebee' : 'inherit'
                }}
            />
        );
    };

    return (
        <>
            <form className="AziendaFrom" onSubmit={handleSubmit(onSubmit)}>
                <div className="AziendaFrom__inner">
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                {...field}
                                label="Nome"
                                fullWidth
                                margin="normal"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    {stabili_id && (
                        <Controller
                            name="prodotti_id"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    options={prodottiOptions || []}
                                    getOptionLabel={(option) => option.commercial_name || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={field.value || null}
                                    onChange={(_, newValue) => {
                                        field.onChange(newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Prodotto"
                                            className="customTextBox autoCompleteTextBox"
                                            margin="normal"
                                            fullWidth
                                            error={!!errors.prodotti_id}
                                            helperText={errors.prodotti_id?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    )}


                    {product_id && (
                        <Controller
                            name="stabili_id"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    options={stabiliOptions || []}
                                    getOptionLabel={(option) => option.nome || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={field.value || null}
                                    onChange={(_, newValue) => {
                                        field.onChange(newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="stabilimento"
                                            className="customTextBox autoCompleteTextBox"
                                            margin="normal"
                                            fullWidth
                                            error={!!errors.stabili_id}
                                            helperText={errors.stabili_id?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    )}


                    <Controller
                        name="tipo"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                select
                                label="Tipo"
                                value={field.value || ''}
                                onChange={field.onChange}
                                fullWidth
                                margin="normal"
                                error={!!errors.tipo}
                                helperText={errors.tipo?.message}
                                SelectProps={{
                                    renderValue: (selected) => renderTipoChip(selected)
                                }}
                            >
                                {tipo.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {renderTipoChip(option)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                    
                    <Controller
                        name="lotto"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                {...field}
                                label="Lotto"
                                fullWidth
                                margin="normal"
                                error={!!errors.lotto}
                                helperText={errors.lotto?.message}
                            />
                        )}
                    />
                    
                    <Controller
                        name="um"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={umOptions || []}
                                value={field.value || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="UM"
                                        className="customTextBox autoCompleteTextBox"
                                        margin="normal"
                                        fullWidth
                                        error={!!errors.um}
                                        helperText={errors.um?.message}
                                    />
                                )}
                            />
                        )}
                    />
                    
                    <Controller
                        name="motivazione"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={motivazioneOptions || []}
                                value={field.value || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Motivazione"
                                        className="customTextBox autoCompleteTextBox"
                                        margin="normal"
                                        fullWidth
                                        error={!!errors.motivazione}
                                        helperText={errors.motivazione?.message}
                                    />
                                )}
                            />
                        )}
                    />

                    <Controller
                        name="displonibile"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                {...field}
                                label="Q.ta disponibile"
                                fullWidth
                                margin="normal"
                                error={!!errors.displonibile}
                                helperText={errors.displonibile?.message}
                                type="number"
                            />
                        )}
                    />
                    
                    <Controller
                        name="formato"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={formatoOptions || []}
                                value={field.value || null}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Formato"
                                        className="customTextBox autoCompleteTextBox"
                                        margin="normal"
                                        fullWidth
                                        error={!!errors.formato}
                                        helperText={errors.formato?.message}
                                    />
                                )}
                            />
                        )}
                    />
                    
                    <Controller
                        name="stato"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                select
                                label="Stato"
                                value={field.value || ''}
                                onChange={field.onChange}
                                fullWidth
                                margin="normal"
                                error={!!errors.stato}
                                helperText={errors.stato?.message}
                            >
                                {stato.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                    
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <Controller
                            name="data_ingresso"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    className="datePickerField"
                                    label="Data Ingresso"
                                    value={field.value}
                                    onChange={(newValue) => field.onChange(newValue)}
                                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                                />
                            )}
                        />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <Controller
                            name="scadenza"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    className="datePickerField"
                                    label="Scadenza"
                                    value={field.value}
                                    onChange={(newValue) => field.onChange(newValue)}
                                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                                />
                            )}
                        />
                    </LocalizationProvider>

                    <Controller
                        name="note"
                        control={control}
                        render={({ field }) => (
                            <Textarea
                                placeholder="Note"
                                {...field}
                            />
                        )}
                    />

                    <div className="btnPart1">
                        <Button type="submit" variant="contained">
                            Salva dati
                        </Button>
                    </div>
                </div>
            </form>

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    {cantine ? "Record updated successfully" : "Record created successfully"}
                </Alert>
            </Snackbar>
        </>
    );
};

export default GiacenzeForm;